import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Grid,
} from "@mui/material"
import { z } from "zod"
import dayjs from "dayjs"
import { useAddModel } from "@/hooks/useAddModel"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import ClearIcon from "@mui/icons-material/Clear"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"

const ExpenseSchema = z.object({
  date: z.string(),
  amount: z.string(),
  title: z.string().min(3),
})

type ExpenseFormValue = z.infer<typeof ExpenseSchema>

interface AddExpenseModalProps {
  open?: boolean
  onClose?: () => void
  defaultValues?: Partial<ExpenseFormValue>
  getNewRecord?: (response: ExpenseFormValue) => void
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({ open, onClose }) => {
  const {
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    control,
    Controller,
    defaultOnClose,
  } = useAddModel<ExpenseFormValue>(
    "expense",
    ExpenseSchema,
    "expense",
    onClose
  )

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: { xs: "95vw", sm: "90vw", md: 500 },
          margin: { xs: "50px auto", md: "100px auto" },
          padding: { xs: 1, sm: 2 },
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
            flexWrap: "wrap",
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
              color: "black",
            }}
          >
            {selectedRow ? "Update Expense" : "Add New Expense"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Box sx={{ paddingY: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="date"
                control={control}
                defaultValue={dayjs().format("YYYY-MM-DD")}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Date"
                    value={field.value ? dayjs(field.value) : dayjs()}
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                      )
                    }}
                    sx={{
                      width: "100%",
                      "& fieldset": {
                        border: "1px solid #D5D9E2",
                        borderRadius: "7px",
                      },
                    }}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Amount"
            sx={{ marginBottom: 2 }}
            defaultValue={selectedRow?.amount || ""}
            error={!!errors.amount}
            helperText={errors.amount?.message?.toString() || ""}
            {...register("amount")}
          />

          <TextField
            fullWidth
            label="Title"
            sx={{ marginBottom: 2 }}
            defaultValue={selectedRow?.title || ""}
            error={!!errors.title}
            helperText={errors.title?.message?.toString() || ""}
            {...register("title")}
          />

          {/* Buttons */}
          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddExpenseModal
